import config from '../config/environment';
import supabase from '../config/supabase';
import {
  parsePaymentsReceivedDashboard,
  type PaymentsReceivedDashboard,
  type PaymentsReceivedQuery,
  type PaymentsReceivedRow,
} from '../utils/paymentsReceivedDashboard';
import { handleServiceError } from '../utils/errorHandler';

const EXPORT_PAGE_SIZE = 100;

class PaymentsReceivedServiceClass {
  async getDashboard(query: PaymentsReceivedQuery): Promise<PaymentsReceivedDashboard> {
    const token = await this.getAccessToken();
    const url = this.buildUrl(query);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });
    const body = await response.json().catch(() => ({})) as { error?: string };

    if (!response.ok) {
      handleServiceError(
        new Error(body.error || 'Erro ao carregar os pagamentos'),
        'carregar os pagamentos',
      );
    }

    const snapshot = parsePaymentsReceivedDashboard(body);
    if (!snapshot) {
      handleServiceError(
        new Error('Erro ao carregar os pagamentos'),
        'carregar os pagamentos',
      );
    }

    return snapshot;
  }

  async getAllForExport(query: Omit<PaymentsReceivedQuery, 'page' | 'pageSize'>): Promise<PaymentsReceivedRow[]> {
    const payments: PaymentsReceivedRow[] = [];
    let page = 1;
    let totalCount = Number.POSITIVE_INFINITY;

    while (payments.length < totalCount) {
      const snapshot = await this.getDashboard({
        ...query,
        page,
        pageSize: EXPORT_PAGE_SIZE,
      });
      totalCount = snapshot.totalCount;
      payments.push(...snapshot.payments);

      if (snapshot.payments.length === 0) {
        break;
      }

      page += 1;
    }

    return payments;
  }

  private buildUrl(query: PaymentsReceivedQuery): URL {
    const url = new URL(`${config.supabaseFunctionsUrl}/payments-received`);
    url.searchParams.set('dateStart', query.dateStart);
    url.searchParams.set('dateEnd', query.dateEnd);
    url.searchParams.set('page', String(query.page));
    url.searchParams.set('pageSize', String(query.pageSize));

    const search = query.searchName.trim();
    if (search) {
      url.searchParams.set('search', search);
    }

    for (const gateway of query.paymentGateway) {
      url.searchParams.append('paymentSystem', gateway);
    }

    return url;
  }

  private async getAccessToken(): Promise<string | undefined> {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token;
  }

  private buildHeaders(token: string | undefined): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: config.supabaseAnonKey,
    };
  }
}

export const PaymentsReceivedService = new PaymentsReceivedServiceClass();
